<template>
  <div>
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10" class="d-flex align-items-center header-section">
        <h3>
          {{ $t('settings.page.context.title') }}
        </h3>
        <b-dropdown
          class="context-menu"
          toggle-class="dot-button"
          no-caret
          no-flip
          right
        >
          <template #button-content>
            <b-icon icon="three-dots-vertical" aria-hidden="true" scale="1"></b-icon>
          </template>
          <b-dropdown-item @click="copySettings">
            {{ $t('dropdowns.copySettings') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="settingsCopied"
            @click="pasteSettings"
          >
            {{ $t('dropdowns.pasteSettings') }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col cols="12" offset-sm="1" sm="10" class="description-section">
        <hr class="mt-0">
        <p class="mb-3">
          {{ $t('settings.page.context.desc') }}
        </p>
      </b-col>
      <b-col cols="12" offset-sm="1" sm="10">
        <div class="context-wrapper">
          <div v-for="(contextObject, contextIndex) in contextData"
               :key="`action-${contextIndex}`"
               class="context mb-5 pt-4">
            <b-row>
              <b-col cols="12">
                <div class="wrapper-box">
                  <h3 class="text-uppercase">
                    {{ contextObject.action | actionMapping(allActions)}} ({{ contextObject.source }} )
                    <b-dropdown
                      toggle-class="dot-button px-0 pt-0"
                      no-caret
                      dropup
                      no-flip
                      left
                    >
                      <template #button-content>
                        <b-icon icon="three-dots-vertical"
                                aria-hidden="true" scale=".8"
                        ></b-icon>
                      </template>
                      <b-dropdown-item
                        @click="removeContext(contextIndex)"
                      >
                        {{ $t('modals.deleteTeamItem.dropdown') }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </h3>
                  <div v-if="contextObject.source === 'segment'">
                    <div class="or-context mb-3">
                      <div class="d-block and-wrapper p-3">
                        <span v-if="contextObject.segmentId">
                          ...{{ $t('settings.page.context.condition.if') }}
                          {{ $t('settings.page.context.condition.source.segment') }}
                          <strong>{{ segmentName(contextObject.segmentId) }}</strong>
                        </span>
                        <b-button
                          class="green-button-transparent border-0 text-uppercase"
                          @click="showItemModal(contextObject.action, contextObject.source)"
                        >
                          {{ contextObject.segmentId ? $t('buttons.update') : $t('buttons.create') }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="contextObject.cases.or.length === 0">
                    <div class="or-context mb-3">
                      <div class="d-block and-wrapper p-3">
                        <b-button
                          class="green-button-transparent border-0 text-uppercase"
                          @click="showItemModal(contextObject.action, contextObject.source)"
                        >
                          {{ $t('buttons.create') }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div class="or-context mb-3"
                         v-for="(orCase, orIndex) in contextObject.cases.or"
                         :key="`${contextObject.action}-or-${orIndex}`">
                      <div class="d-block and-wrapper p-3">
                        <div v-for="(andCase, andIndex) in orCase.and"
                             :key="`${contextObject.action}-or-${orIndex}-and-${andIndex}`"
                             class="and-box box p-1 pl-3 mb-3 text-white position-relative"
                        >
                          <span
                            v-if="conditionParts(andCase).path
                              === 'personalized_variable_set_id'"
                          >
                          ...{{ $t('settings.page.context.condition.if') }}
                          {{ $t(`settings.page.context.condition.item`) }}
                          </span>
                          <span v-else>
                            ...{{ $t('settings.page.context.condition.if') }}
                          {{ $t(`settings.page.context.condition.source.${contextObject.source}`) }}
                          {{ $t('settings.page.context.condition.value') }}
                          <strong>{{
                              conditionParts(andCase).path
                                | printName(personalizationValues)
                            }}</strong>
                          </span>
                          <span
                            v-if="conditionParts(andCase).path
                              === 'personalized_variable_set_id'"
                          >
                          {{
                            $t(
                            `settings.page.context.condition.operatorSet.${conditionParts(andCase).operator}`
                            )
                          }}
                            <strong>{{ getSetName(conditionParts(andCase).value) }}</strong>
                          </span>
                          <span v-else-if="conditionNeedsValueDisplay(andCase)">
                          {{
                            $t(
                              `settings.page.context.condition.operator.${conditionParts(andCase).operator}`
                            )
                          }}
                          <strong>{{ conditionParts(andCase).value }}</strong>
                          </span>
                          <span v-else>
                          {{
                            $t(
                              `settings.page.context.condition.operator.${conditionParts(andCase).operator}`
                            )
                          }}
                          </span>
                          <div class="close"
                               @click="removeElement(
                       {action: contextIndex, or: orIndex, and: andIndex}
                       )"
                          >
                            <b-icon
                              icon="x"
                              style="color: #000;"
                              scale="1.5"
                              aria-hidden="true"
                              class="top--1 mr-1"
                            ></b-icon>
                          </div>
                          <div class="edit"
                               @click="updateElement(
                       {action: contextIndex, or: orIndex, and: andIndex}
                       )"
                          >
                            <b-icon
                              icon="pencil"
                              style="color: #000;"
                              scale="0.8"
                              aria-hidden="true"
                              class="top--1 mr-1"
                            ></b-icon>
                          </div>
                        </div>
                        <b-button
                          class="green-button-transparent border-0 text-uppercase"
                          @click="showItemModal(contextObject.action, contextObject.source, orIndex)"
                        >
                          {{ $t('buttons.and') }}
                        </b-button>
                      </div>
                    </div>
                    <b-button
                      class="green-button-transparent border-0 text-uppercase"
                      @click="showItemModal(contextObject.action, contextObject.source)"
                    >
                      {{ $t('buttons.or') }}
                    </b-button>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <div class="new-value-box px-3 py-3 mb-4" v-if="availableCombinations.length > 0">
          <b-row>
            <b-col cols="8">
              <h3 class="mb-0">{{ $t('settings.page.context.newTitle') }}</h3>
              <p class="mb-0" v-html="$t('settings.page.context.newDesc')"></p>
            </b-col>
            <b-col cols="4" class="d-flex align-items-center justify-content-center">
              <b-button
                class="btn px-4 green-button-transparent border-0 btn-secondary text-center"
                @click="showNewContextModal = true"
              >
                <b-icon
                  icon="plus-circle-fill"
                  class="mr-2 top--3"
                  aria-hidden="true"
                  scale="1"
                ></b-icon>
                {{ $t('settings.page.context.newButton') }}
              </b-button>
            </b-col>
          </b-row>
        </div>
        <div v-if="showNewContextModal">
          <modal-create-new-context
            :all-actions="allActions"
            :source-options="sourceOptions"
            :event-options="eventOptions"
            :hide-action-choice="hideActionChoice"
            :available-combinations="availableCombinations"
            @create-context="createContext"
            @cancel-modal="showNewContextModal = false"
          />
        </div>
        <div v-if="showContextItemModal">
          <modal-create-context-item
            :personalization-values="personalizationValues"
            :personalization-set-data="personalizationSetData"
            :forms-data="formsData"
            :segments="segments"
            :action="selectedAction"
            :or-index="selectedOrIndex"
            :and-index="selectedAndIndex"
            :source="selectedSource"
            :selected-left-value="selectedLeftValue"
            :selected-operator="selectedOperator"
            :selected-right-value="selectedRightValue"
            :is-element-update="isElementUpdate"
            @create-context="createContextItem"
            @update-context="finalizeUpdateElement"
            @cancel-modal="cancelContextItemModal"
          />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { keepUnchangedOperation, parseOperation } from '@/components/modals/contextRuleHelpers';

export default {
  name: 'PageSettings',
  components: {
    ModalCreateNewContext: () => import('@/components/modals/ModalCreateNewContext.vue'),
    ModalCreateContextItem: () => import('@/components/modals/ModalCreateContextItem.vue'),
  },
  props: {
    contextData: {
      type: Array,
      required: true,
    },
    personalizationValues: {
      type: Array,
    },
    formsData: {
      type: Array,
      default: () => [],
    },
    allActions: {
      type: Array,
      default: () => [],
    },
    sourceOptions: {
      type: Array,
      default: () => [],
    },
    // Only supplied for pages (PageSettings.vue); the element personalization
    // modal (ModalPersonalizationEditor.vue) never passes this, so its
    // 'hidePage' action never exists there and the event picker stays hidden.
    eventOptions: {
      type: Array,
      default: () => [],
    },
    // Named segments (theme_settings.segments) — [{ id, name, cases }].
    // Resolves a segment-sourced rule's segmentId to a display name here,
    // and is forwarded to ModalCreateContextItem for the segment picker.
    segments: {
      type: Array,
      default: () => [],
    },
    // Passed straight through to ModalCreateNewContext — see the prop
    // comment there. Only ProjectSettingsSegments sets it, for the segment's
    // own condition, where an action has no meaning and is thrown away.
    hideActionChoice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showNewContextModal: false,
      showContextItemModal: false,
      selectedAction: null,
      selectedSource: null,
      selectedOrIndex: null,
      settingsCopied: false,
      selectedAndIndex: null,
      isElementUpdate: false,
      selectedLeftValue: null,
      selectedOperator: null,
      selectedRightValue: null,
    };
  },
  computed: {
    availableCombinations() {
      return this.possibleSourcesActionCombinations.filter((combination) => {
        const { action, source } = combination;
        return !this.contextData.some(
          (context) => context.action === action && context.source === source,
        );
      });
    },
    possibleSourcesActionCombinations() {
      const combinations = [];
      this.allActions.forEach((action) => {
        this.sourceOptions.forEach((source) => {
          if (!source.disabled && !action.disabled) {
            combinations.push({
              action: action.value,
              source: source.value,
            });
          }
        });
      });
      return combinations;
    },
    personalizationSetData() {
      const personalizationSetData = [];
      this.personalizationValues.forEach((set) => {
        personalizationSetData.push({
          value: set.setId,
          text: set.name,
        });
      });
      personalizationSetData.sort((a, b) => a.text.localeCompare(b.text));
      return [
        { value: null, text: this.$t('settings.page.context.selectPersonalizationSet') },
        ...personalizationSetData,
      ];
    },
  },
  filters: {
    printName(value, personalizationObject) {
      // split value
      const [setId, valueString] = value.split('.');
      // find the valueString inside the personalizationObject
      const setObject = personalizationObject.find((set) => set.setId === Number(setId));
      if (setObject) {
        // find the name of the element
        const itemObject = setObject.values.find((item) => item.id === valueString);
        if (itemObject) return `${setObject.name}:${itemObject.name}`;
      }
      return value;
    },
    actionMapping(value, allActions) {
      const actionName = allActions.find((action) => action.value === value);
      return (actionName) ? actionName.text : value;
    },
  },
  created() {
    console.log('contextData', this.contextData);
    console.log('personalizationSetData', this.personalizationSetData);
    console.log('personalizationValues', this.personalizationValues);

    if (localStorage.getItem('context_settings')) {
      this.settingsCopied = true;
    }
  },
  methods: {
    createContext(context) {
      this.showNewContextModal = false;
      this.$emit('context-create', context);
    },
    createContextItem(context) {
      this.showContextItemModal = false;
      this.$emit('context-update', context);
    },
    updateElement(element) {
      this.isElementUpdate = true;
      this.selectedAction = this.contextData[element.action].action;
      this.selectedSource = this.contextData[element.action].source;
      this.selectedActionIndex = element.action;
      this.selectedOrIndex = element.or;
      this.selectedAndIndex = element.and;
      const contextElement = this.contextData[element.action].cases.or[element.or].and[element.and];
      const parsed = this.conditionParts(contextElement);
      this.selectedLeftValue = parsed.path;
      this.selectedOperator = parsed.operator;
      this.selectedRightValue = parsed.value !== undefined && parsed.value !== null ? parsed.value.toString() : '';
      this.showContextItemModal = true;
    },
    // Reads a stored operation into { path, operator, value } for both
    // display and editing. Delegates to parseOperation, which — unlike the
    // old inline `[leftValue, rightValue] = contextElement[operator]`
    // destructuring — also understands the 'in' operator's reversed
    // arguments and the valueless '!!'/'!' operators.
    conditionParts(andCase) {
      const key = Object.keys(andCase)[0];
      return parseOperation(andCase) || { path: andCase[key][0]?.var, operator: key, value: andCase[key][1] };
    },
    conditionNeedsValueDisplay(andCase) {
      const { operator } = this.conditionParts(andCase);
      return operator !== '!!' && operator !== '!';
    },
    finalizeUpdateElement(operation) {
      this.showContextItemModal = false;
      // Replace the entire object at the specified indices
      if (this.contextData[this.selectedActionIndex]
        && this.contextData[this.selectedActionIndex].cases.or[this.selectedOrIndex]
        && this.contextData[this.selectedActionIndex].cases.or[this.selectedOrIndex].and[this.selectedAndIndex]) {
        const stored = this.contextData[this.selectedActionIndex]
          .cases.or[this.selectedOrIndex].and[this.selectedAndIndex];
        // "Übernehmen" without changing anything must leave the stored rule
        // alone. The modal always hands back a freshly built operation, and a
        // fresh build is the CURRENT shape -- for '!!' / '!' no longer the
        // shape a rule from before the answered fix carries. Writing it back
        // would flip the page's visibility for every visitor who answered 0 or
        // "Nein", without anybody having asked for it.
        this.contextData[this.selectedActionIndex]
          .cases.or[this.selectedOrIndex].and[this.selectedAndIndex] = keepUnchangedOperation(stored, operation);
      }
      // reset the selected values
      this.resetSelectedValues();
      this.$emit('context-element-update', this.contextData);
    },
    cancelContextItemModal() {
      this.showContextItemModal = false;
      this.resetSelectedValues();
    },
    resetSelectedValues() {
      this.isElementUpdate = false;
      this.selectedLeftValue = null;
      this.selectedOperator = null;
      this.selectedRightValue = null;
      this.selectedOrIndex = null;
      this.selectedAndIndex = null;
      this.selectedAction = null;
      this.selectedSource = null;
    },
    removeElement(element) {
      this.$emit('remove-element', element);
    },
    removeContext(contextIndex) {
      this.$emit('remove-context', contextIndex);
    },
    showItemModal(action, source, orIndex) {
      this.selectedOrIndex = orIndex;
      this.selectedAction = action;
      this.selectedSource = source;
      this.showContextItemModal = true;
    },
    getSetName(id) {
      const set = this.personalizationSetData.find((element) => element.value === id);
      if (set) return set.text;
      return id;
    },
    segmentName(id) {
      const segment = this.segments.find((s) => s.id === id);
      return segment ? segment.name : id;
    },
    copySettings() {
      localStorage.setItem('context_settings', JSON.stringify(this.contextData));
      this.settingsCopied = true;
    },
    pasteSettings() {
      const newSettings = localStorage.getItem('context_settings');
      localStorage.removeItem('context_settings');
      this.settingsCopied = false;
      this.$emit('context-copy', JSON.parse(newSettings));
    },
  },
};
</script>

<style scoped lang="scss">
.and-wrapper {
  border: 1px solid $site-font-color;
  .and-box {
    padding-right: 60px !important;
  }
}

.box {
  background-color: #53575B;
}
.new-value-box {
  background-color: #53575B;
  p {
    color: #fff;
    font-size: 12px;
  }
}
::v-deep  .dot-button {
  background-color: transparent;
  border: none;
}
::v-deep  .context-menu .dot-button {
  position: relative;
  top: -5px;
}
.close, .edit {
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 4px;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: .5;
}
.edit {
  right: 28px;
  top: -1px;
}
</style>
